import "./auditPage.css";
import { useEffect, useState } from "react";
import {
  Row,
  Col,
  List,
  Pagination,
  Input,
  DatePicker,
  Select,
  Spin,
  Tooltip,
  Button,
  Tag
} from "antd";
import { fetchAuditsByOrg, fetchAuditsByUser } from "../../services";
import { useDispatch, useSelector } from "react-redux";
import { format, parseISO } from "date-fns";
import { ReactComponent as XIcon } from "../../assets/x.svg";
import { ReactComponent as FilterIcon } from "../../assets/filter-lines.svg";
import { ReactComponent as SearchIcon } from "../../assets/search-short-handle.svg";
import { ReactComponent as ArrowRight } from "../../assets/arrow-right.svg";
import { ReactComponent as ArrowLeft } from "../../assets/arrow-left.svg";
import {
  getUserOptions,
  renderIdOptions,
  renderOptions,
  tagRenderAssessment
} from "../../util/basicRenders";
import dayjs from "dayjs";
import { filterAudits } from "../../util/filters";
import { SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { orderBy } from "lodash";
import { setVariable } from "../../store/forms/actions";

const AuditPage = () => {
  const dispatchito = useDispatch();
  const navigate = useNavigate();
  const { Search } = Input;
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const prefix = <SearchIcon style={{ fontSize: 24, color: "purple" }} />;

  const user = useSelector(state => state.user);
  const organizationId = useSelector(
    state => state.organizations.organizationId
  );
  const mainObligations = useSelector(
    state => state.obligations?.obligations ?? []
  );
  const extraObligations = useSelector(
    state => state.internalObligations?.internalObligations ?? []
  );
  const mainDocuments = useSelector(state => state.documents?.mainObligations);
  const extraDocuments = useSelector(
    state => state.documents?.internalObligations
  );
  const users = useSelector(state => state.organizations?.usersData);
  const uniqueMainCategories = useSelector(state => state.laws.lawCategories);
  const historyButtonInput = useSelector(state => state.forms?.historySearch);

  const allItems = {
    mainObligations,
    extraObligations,
    mainDocuments,
    extraDocuments
  };
  const sortedUniqueMainCategories = uniqueMainCategories
    ? uniqueMainCategories.toSorted((a, b) => a.label.localeCompare(b.label))
    : [];

  const [openFilters, setOpenFilters] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState(user?.role === 'assignedUser' ? [user?.userId] : []);
  const [selectedAssessment, setSelectedAssessment] = useState([]);
  const [chosenType, setChosenType] = useState("");
  const [selectedFramework, setSelectedFramework] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [allAudits, setAllAudits] = useState([]);
  const [noData, setNoData] = useState(false);
  const [filteredAudits, setFilteredAudits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [chosenRange, setChosenRange] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [searchID, setSearchID] = useState("");
  const [currentGroupedLogsContent, setCurrentGroupedLogsContent] = useState(
    []
  );
  const [loading, setLoading] = useState(true);

  // State for current page and items per page (for pagination)
  const itemsPerPage = 14; //const [itemsPerPage, setItemsPerPage] = useState(4);
  const [currentPage, setCurrentPage] = useState(1);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const displayedItems =
    filteredAudits && filteredAudits.slice(startIndex, endIndex);


  useEffect(() => {
    if (historyButtonInput) {
      setSearchTerm(historyButtonInput);
      dispatchito(setVariable({key: 'historySearch', value: ''}))
    }
  }, [historyButtonInput]);

  useEffect(() => {
    if (user && user?.role === 'assignedUser') setSelectedUsers([user?.userId]);
  }, [user]);

  function clearAllFilters() {
    setSearchTerm("");
    setSearchUser("");
    setChosenRange([]);
    setChosenType("");
    setSelectedAssessment([]);
    setSelectedUsers(user?.role === 'assignedUser' ? [user?.userId] : []);
    setSelectedIds([]);
    setSelectedFramework([]);
  }

  function getDay(date) {
    const day = format(date, "dd-MM-yyyy");
    const today = format(new Date(), "dd-MM-yyyy");
    if (day === today) {
      return "Today";
    }
    return format(date, "EEEE");
  }

  function getType(str) {
    if (str.includes("create")) {
      return "insert";
    } else if (str.includes("delete")) {
      return "delete";
    }
    return "update";
  }

  function setID(value) {
    //find what id it is
    let navigationId = "";
    let type = "";
    if (mainObligations) {
      if (mainObligations?.find(o => o?._id === value)) {
        navigationId = value;
        type = "main";
      }
    }
    if (!navigationId && extraObligations) {
      if (extraObligations?.find(o => o?._id === value)) {
        navigationId = value;
        type = "extra";
      }
    }
    //if not found in any obligation check the documents and get their obligation_id
    if (!navigationId && mainDocuments) {
      console.debug("searching main documents")

      for (const doc in mainDocuments) {
        if (mainDocuments?.[doc]?.find(d => d?._id === value)) {
          navigationId = doc?.obligation_id;
          type = "main";
          break;
        }
      }
    }
    if (!navigationId && extraDocuments) {
      for (const doc in extraDocuments) {
        if (extraDocuments?.[doc]?.find(d => d?._id === value)) {
          navigationId = doc?.obligation_id;
          type = "extra";
          break;
        }
      }
    }

    if (navigationId) {
      const page = type === "main"
      ? user?.role === "assignedUser"
        ? "/my-tasks"
        : "/my-checklist"
      : "/internal-obligations";
      console.debug({page})
      dispatchito({
        type: type === "main" ? "SET_NAVIGATE_ID" : "SET_NAVIGATE_INTERNAL_ID",
        payload: type === "main" ? { value: navigationId } : navigationId
      });
      navigate(page);
    }
  }

  function getDescription(log) {
    // console.debug({ log });
    // console.debug(log?.action?.includes("#"));
    if (log?.action?.includes("#")) {
      return `<p>${log.action
        .replace(
          "#user#",
          `<span class="bold-text">
        ${log?.user_name ?? "..."}
      </span>`
        ) //this will be a link later like in home page navigating to obligation in checklist later.
        .replace(
          "#object#",
          `<span class="bold-text">${log?.object_description ?? "..."}</span>`
        )}</p>`;
    } else {
      return `<p>
      The user
      <span class="bold-text">
        ${log?.user_name ?? ""}
      </span>
      ${log?.action ?? "did"}
      <span class="bold-text">
        ${log?.object_description ?? "something."}
      </span>
    </p>`;
    }
  }

  const onChangeDate = e => {
    // console.debug("RANGE", e);
    let from =
      e && e[0]
        ? e[0]?.$y +
          "-" +
          (e[0]?.$M + 1).toString().padStart(2, "0") +
          "-" +
          e[0]?.$D.toString().padStart(2, "0")
        : "";
    let to =
      e && e[1]
        ? e[1]?.$y +
          "-" +
          (e[1]?.$M + 1).toString().padStart(2, "0") +
          "-" +
          e[1]?.$D.toString().padStart(2, "0")
        : "";
    // console.debug("RANGE change on date", from, to);
    let newRange = [from, to];
    setChosenRange(newRange);
  };

  //fetch all audits if not already fetched
  // useEffect(() => {
  //   if (user && !isEmpty(organizationId) && !noData && !allAudits.length) {
  //     setLoading(true);
  //     fetchAuditsByOrg(
  //       user,
  //       organizationId
  //     )
  //       .then(data => {
  //         setNoData(!data.length);
  //         if (!data.length) setLoading(false);
  //         setAllAudits(data);
  //       })
  //       .catch(error => console.log(error));
  //   }
  // }, [user, allAudits]);

  //fetch all audits on company change
  useEffect(() => {
    setLoading(true);
    // setNoData(false);
    if (organizationId) {
      fetchAuditsByOrg(user, organizationId)
        .then(data => {
          setNoData(!data.length);
          if (!data.length) {
            setFilteredAudits(data);
            setCurrentGroupedLogsContent(data);
          }
          setLoading(false);
          setAllAudits(data);
        })
        .catch(error => console.error("AuditPage - fetchAuditsByOrg", error));
    } else if (user?.role === "masterEditor") {
      fetchAuditsByUser(user)
        .then(data => {
          setNoData(!data.length);
          if (!data.length) {
            setFilteredAudits(data);
            setCurrentGroupedLogsContent(data);
          }
          setLoading(false);
          setAllAudits(data);
        })
        .catch(error => console.error("AuditPage - fetchAuditsByUser", error));
    }
  }, [user, organizationId]);

  //filter the audits
  useEffect(() => {
    if (allAudits.length) {
      if (
        searchTerm?.length ||
        chosenRange?.length ||
        chosenType?.length ||
        selectedAssessment?.length ||
        selectedUsers?.length ||
        selectedIds?.length ||
        selectedFramework?.length
      ) {
        const filters = {
          searchTerm,
          chosenRange,
          chosenType,
          selectedAssessment,
          selectedUsers,
          selectedIds,
          selectedFramework
        };
        console.debug({ filters });
        const newFilteredAudits = filterAudits(allAudits, filters, allItems);
        setFilteredAudits(newFilteredAudits);
      } else {
        setFilteredAudits(allAudits);
      }
      setCurrentPage(1);
      setCurrentGroupedLogsContent([]);
    }
  }, [
    allAudits,
    searchTerm,
    chosenRange,
    chosenType,
    selectedAssessment,
    selectedUsers,
    selectedIds,
    selectedFramework
  ]);

  //group the displayed items when they are first set
  useEffect(() => {
    if (displayedItems?.length && !currentGroupedLogsContent.length) {
      const obj = Object.groupBy(displayedItems, ({ createdAt }) =>
        format(parseISO(createdAt), "yyyyMMdd")
      );
      console.debug(Object.values(obj))
      setCurrentGroupedLogsContent(orderBy(Object.values(obj), function(o) {return o?.[0]?.createdAt}, ['desc']));
      setTimeout(() => setLoading(false), 8000);
      // console.debug({ currentGroupedLogs });
      // console.debug({ currentGroupedLogsContent });
      // console.debug(Object.values(currentGroupedLogs));
    }
  }, [displayedItems, currentGroupedLogsContent]);

  //group the displayed items on every page change
  useEffect(() => {
    if (displayedItems?.length) {
      const obj = Object.groupBy(displayedItems, ({ createdAt }) =>
        format(parseISO(createdAt), "yyyyMMdd")
      );
      setCurrentGroupedLogsContent(orderBy(Object.values(obj), function(o) {return o?.[0]?.createdAt}, ['desc']));
      // console.debug({ currentGroupedLogs });
      // console.debug({ currentGroupedLogsContent });
      // console.debug(Object.values(currentGroupedLogs));
    }
  }, [currentPage]);

  return (
    <div id="audit-page" className="wrapper universe-container">
      {" "}
      {/**universe-container class added for convenience */}
      <aside id="audit-sidebar" className="flex-item">
        <Row>
          <Col span={22}>
            {" "}
            <Search
              className="filters-search-bar"
              id={"filters-search-bar"}
              placeholder={`Search by User, Obligation`}
              prefix={prefix}
              allowClear
              size="large"
              value={searchTerm || ""}
              onSearch={e => setSearchTerm(e)}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </Col>
          <Col span={2} className="filter-icon">
            <Tooltip
              title={
                <>
                  Click here
                  <br />
                  to {openFilters ? "hide" : "show"}{" "}
                  the filters
                </>
              }
              arrow={true}
              color={"#FFFFFF"}
              overlayClassName="filter-tooltip"
              placement="top"
            >
              <FilterIcon
                onClick={() => setOpenFilters(!openFilters)}
                style={{ marginTop: "0.5rem", cursor: "pointer" }}
              />
            </Tooltip>
          </Col>
        </Row>
        <Row
          gutter={[16, 16]}
          style={{ display: openFilters ? "flex" : "none" }}
        >
          <Col span={8}>
            {" "}
            <Select
              virtual={false}
              mode="multiple"
              id={`audit_as_filter`}
              closable
              allowClear
              tagRender={option => tagRenderAssessment(option)}
              placeholder="Assessment"
              className="assessment-select-filter row-2-filter"
              popupClassName="obl-filter-select-dropdown"
              onChange={value => {
                setSelectedAssessment(value);
              }}
              value={selectedAssessment}
              options={[
                {
                  value: "Pending",
                  label: "Pending"
                },
                {
                  value: "Partially",
                  label: "Partially Compliant"
                },
                {
                  value: "Compliant",
                  label: "Compliant"
                },
                {
                  value: "Non-Compliant",
                  label: "Non Compliant"
                },
                {
                  value: "na",
                  label: "N/A"
                }
              ]}
            />
          </Col>
          <Col span={8}>
            {" "}
            <Select
              virtual={false}
              id="select-audit-obl-type"
              className="assessment-select-filter row-2-filter"
              allowClear
              value={chosenType || undefined}
              placeholder="Obligation Type (Main-Internal)"
              onChange={selection => {
                console.debug(selection ?? "cleared");
                setChosenType(selection ?? "");
              }}
            >
              <Option value="main">Main Obligations</Option>
              <Option value="extra">Internal Obligations</Option>
            </Select>
          </Col>
          <Col span={8}>
            {" "}
            <Select
              virtual={false}
              mode="multiple"
              // popupClassName="category-selector"
              value={selectedFramework}
              allowClear
              tagRender={option => {
                const { closable, onClose, label } = option;
                return (
                  <Tag
                    color={"#F1EAFA"}
                    className="filter-assessment-tag"
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginInlineEnd: 4,
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      fontSize: "0.75rem",
                      color: "#725798",
                      borderRadius: "1rem",
                      "& .ant-tag-close-icon": {
                        color: "#725798"
                      }
                    }}
                  >
                    {label}
                  </Tag>
                );
              }}
              id="complianceCat"
              placeholder="Framework"
              onChange={value => setSelectedFramework(value)}
              className="assessment-select-filter row-2-filter"
            >
              {sortedUniqueMainCategories?.map((item, index) => {
                return (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                );
              })}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              virtual={false}
              className="row-2-filter input-filter userSelect audit-page pseudosearch"
              mode="multiple"
              style={{ width: "100%" }}
              disabled={false}
              showSearch
              suffixIcon={<SearchOutlined />}
              onSearch={setSearchID}
              allowClear
              value={selectedIds}
              placeholder="ID"
              optionFilterProp="children"
              notFoundContent={searchTerm?.length > 3 ? "default" : null}
              onChange={value => {
                setSearchID("");
                setSelectedIds(value);
              }}
              tagRender={option => {
                const { closable, onClose, label } = option;
                return (
                  <Tag
                    color={"#F1EAFA"}
                    className="filter-assessment-tag"
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginInlineEnd: 4,
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      fontSize: "0.75rem",
                      color: "#725798",
                      borderRadius: "1rem",
                      "& .ant-tag-close-icon": {
                        color: "#725798"
                      }
                    }}
                  >
                    {label}
                  </Tag>
                );
              }}
              filterOption={false}
            >
              {renderIdOptions(searchID, mainObligations, extraObligations)}
            </Select>
          </Col>
          <Col span={8}>
            <Select
              virtual={false}
              className="row-2-filter input-filter userSelect audit-page pseudosearch"
              mode="multiple"
              style={{ width: "100%" }}
              disabled={user?.role === "assignedUser" ? true : false}
              showSearch
              onSearch={setSearchUser}
              allowClear={user?.role === "assignedUser" ? false : true}
              value={user?.role === "assignedUser" ? [user?.userId] : selectedUsers}
              placeholder="Assignee"
              optionFilterProp="children"
              onChange={value => {
                setSearchUser("");
                setSelectedUsers(value);
              }}
              tagRender={option => {
                const options = getUserOptions(users);
                const { closable, onClose } = option;
                return (
                  <Tag
                    color={"#F1EAFA"}
                    className="filter-assessment-tag"
                    closable={closable}
                    onClose={onClose}
                    style={{
                      marginInlineEnd: 4,
                      fontWeight: 400,
                      fontFamily: "Roboto",
                      fontSize: "0.75rem",
                      color: "#725798",
                      borderRadius: "1rem",
                      "& .ant-tag-close-icon": {
                        color: "#725798"
                      }
                    }}
                  >
                    {options?.find(o => o.value === option?.value)?.label}
                  </Tag>
                );
              }}
              filterOption={false}
            >
              {renderOptions(searchUser, users)}
            </Select>
          </Col>
          <Col span={5}>
            {" "}
            <RangePicker
              className="row-2-filter"
              id="universe-range-picker"
              onChange={onChangeDate}
              format={"DD/MM/YYYY"}
              placeholder={["From", "To"]}
              allowEmpty={[true, true]}
              value={[
                chosenRange?.[0] ? dayjs(chosenRange?.[0], "YYYY-MM-DD") : null,
                chosenRange?.[1] ? dayjs(chosenRange?.[1], "YYYY-MM-DD") : null
              ]}
            />
          </Col>
          <Col span={3}>
            {" "}
            <Button
              id="clear-filters-auditpage"
              icon={<XIcon className="purple-svg" />}
              onClick={() => clearAllFilters()}
            >
              Clear Filters
            </Button>
          </Col>
        </Row>
      </aside>
      <div id="filter-content-line" />
      <main id="audit-main-content" className="flex-item">
        {loading && <Spin size="large" />}
        {!loading && currentGroupedLogsContent && (
          <List
            className=""
            itemLayout="vertical"
            dataSource={currentGroupedLogsContent}
            renderItem={(daylogs, index) => {
              // console.debug({ daylogs });
              return (
                <List.Item
                  extra={
                    <>
                      {" "}
                      {daylogs.length &&
                        daylogs.map(log => {
                          return (
                            <div className="log-wrapper">
                              <div className="log-time">
                                <span>
                                  {log?.createdAt &&
                                    parseISO(log?.createdAt) &&
                                    format(
                                      parseISO(log?.createdAt),
                                      "kk:mm"
                                    )}
                                </span>
                              </div>
                              <div
                                onClick={() => setID(log?.object_id)}
                                style={{ cursor: "pointer" }}
                                className={`log-description ${getType(
                                  log?.type ?? log?.action
                                )}`}
                                dangerouslySetInnerHTML={{
                                  __html: getDescription(log)
                                }}
                              ></div>
                            </div>
                          );
                        })}
                    </>
                  }
                >
                  <List.Item.Meta
                    title={getDay(
                      parseISO(daylogs?.[0]?.createdAt) ?? new Date()
                    )}
                    description={
                      <>
                        <span
                          id={`day-section-${
                            format(
                              parseISO(daylogs?.[0]?.createdAt) ?? new Date(),
                              "dd-MM-yyyy"
                            ) ?? "0"
                          }`}
                        >
                          {daylogs?.[0]?.createdAt &&
                            parseISO(daylogs?.[0]?.createdAt) &&
                            format(
                                parseISO(daylogs?.[0]?.createdAt) ?? new Date(),
                                "dd/MM/yyyy"
                                //"d MMM yyyy"
                              )
                            }
                        </span>
                      </>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
        {!loading && (
          <Pagination
            current={currentPage}
            total={filteredAudits ? filteredAudits.length : 0}
            pageSize={itemsPerPage}
            hideOnSinglePage={true}
            showSizeChanger={false}
            onChange={page => setCurrentPage(page)} // Handle page change
            itemRender={(page, type, originalElement) => {
              //custom render is used in order to be able to add ids on next and previous buttons for qa automation
              if (type === "next") {
                return (
                  <button
                    id="audit-pagination-next"
                    className="ant-pagination-item-link"
                    type="button"
                    tabindex="-1"
                  >
                    <span
                      role="img"
                      aria-label="right"
                      className="anticon anticon-right"
                      id="audit-page-icon-right"
                    >
                      <span className="pagination-label">Next</span>
                      <ArrowRight />
                    </span>
                  </button>
                );
              } else if (type === "prev") {
                return (
                  <button
                    id="audit-pagination-prev"
                    type="button"
                    className="ant-pagination-item-link"
                    tabindex="-1"
                  >
                    <span
                      role="img"
                      aria-label="left"
                      id="audit-page-icon-left"
                      className="anticon anticon-left"
                    >
                      <ArrowLeft />
                      <span className="pagination-label">Previous</span>
                    </span>
                  </button>
                );
              }
              return originalElement;
            }}
          />
        )}
      </main>
    </div>
  );
};

export default AuditPage;
